import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import ChevronUpIcon from "../../assets/img/chevron_up.svg";
import ChevronDownIcon from "../../assets/img/chevron_down_sm.svg";
import StackedLegacyPromoGroup from "./StackedLegacyPromoGroup";
import StackedDerivedRateGroup from "./StackedDerivedRateGroup";
import RateplanAmenities from "./RateplanAmenities";
import { selectSelectedRateplanCurrency } from "../../slices/rateplanSlice";
import { buildStackedPromotionGroups, filterVisiblePromos } from "./utils";
import { PROMOTIONS_CARD_TITLE, MAX_STACK_INDENT_DEPTH } from "./constants";
import "./promotion-summary.scss";
import "./stacked-promotion-summary.scss";

const StackedPromotionSummary = ({
  promos = [],
  rateplan,
  date,
  derivedRates = [],
}) => {
  const currency = useSelector(selectSelectedRateplanCurrency);
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    data: { isRefundable, isBreakfastIncluded },
  } = rateplan ?? { data: {} };
  const dayOfWeek = new Date(date).getDay() + 1;

  const visiblePromos = useMemo(
    () => filterVisiblePromos(promos, date, dayOfWeek),
    [promos, date, dayOfWeek]
  );
  // `derivedRates` arrive pre-filtered for date/roomtype applicability —
  // both panels pass only applicable rates (SpecificDateRightPanel filters by
  // roomTypeIds, stayDates and blackOutDates; BulkUpdateRightPanel by month
  // overlap) — so no date check is repeated here, same as the legacy path.
  const groups = useMemo(
    () => buildStackedPromotionGroups(derivedRates, MAX_STACK_INDENT_DEPTH),
    [derivedRates]
  );
  // Count only what actually renders: rates nested beyond the depth cap are
  // hidden, so they must not inflate the badge.
  const rowCount =
    visiblePromos.length +
    groups.reduce((count, group) => count + 1 + group.descendants.length, 0);

  return (
    <div className="stacked-promotions">
      {rowCount > 0 ? (
        <div className="stacked-promotions__card">
          <button
            type="button"
            className="stacked-promotions__header"
            data-heap-id="stacked-promotions-toggle"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <span className="stacked-promotions__title">
              {PROMOTIONS_CARD_TITLE}
            </span>
            <span className="stacked-promotions__count">{rowCount}</span>
            <img
              src={isExpanded ? ChevronUpIcon : ChevronDownIcon}
              className="stacked-promotions__chevron"
              alt={isExpanded ? "collapse" : "expand"}
            />
          </button>
          {isExpanded ? (
            <div className="stacked-promotions__body">
              {visiblePromos.map(({ promo, rule }) => (
                <StackedLegacyPromoGroup
                  key={`promo-${promo.id}`}
                  promo={promo}
                  rule={rule}
                  dayOfWeek={dayOfWeek}
                  currency={currency}
                />
              ))}
              {groups.map(({ parent, descendants }) => (
                <StackedDerivedRateGroup
                  key={`dr-${parent.id}`}
                  parent={parent}
                  descendants={descendants}
                  dayOfWeek={dayOfWeek}
                  currency={currency}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <RateplanAmenities
        isBreakfastIncluded={isBreakfastIncluded}
        isRefundable={isRefundable}
      />
    </div>
  );
};

StackedPromotionSummary.propTypes = {
  promos: PropTypes.array,
  derivedRates: PropTypes.array,
  rateplan: PropTypes.shape({
    data: PropTypes.shape({
      isRefundable: PropTypes.bool,
      isBreakfastIncluded: PropTypes.bool,
    }),
  }),
  date: PropTypes.instanceOf(dayjs),
};

export default StackedPromotionSummary;
